import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { reconcileGrid } from '../devices/device-grid';
import { formatClock } from '../core/format';
import { deviceNameHtml, deviceSubtitlesHtml } from '../devices/device-card';
import { showInfoModal } from '../devices/device-modals';
import { showClickerRestartModal } from '../clicker/clicker-restart-modal';
import { openClickerOperationsWindow } from '../clicker/clicker-operations-modal';
import { openClickerLogWindow } from '../clicker/clicker-info-modal';
import { can } from '../core/access';
import { clickerStartSvg, clickerStopSvg, clickerSettingsSvg, clickerRegistrySvg, clickerLogSvg, infoSvg, browserGlyphSvg, } from '../stream/stream-controls-shared';
const POLL_MS = 15000;
let poll = null;
// Last fetched list + the profile search, so typing re-filters without another fetch.
let lastDevices = [];
let lastFarmId = null;
let profileQuery = '';
function subtitleData(d) {
    return { clickerRunning: d.enabled, clickerApp: d.appName || undefined, clickerTask: d.taskType || undefined, clickerFlow: d.flowName || undefined };
}
function showAdspowerInfoModal(d) {
    const rows = [
        { label: t('farmdev.adspower_profile_id'), value: d.udid },
        { label: t('farmdev.adspower_browser'), value: d.browserOpen ? t('farmdev.adspower_browser_open') : t('farmdev.adspower_browser_closed') },
        { label: t('farmdev.adspower_flow'), value: d.flowName || null },
        { label: t('farmdev.adspower_app'), value: d.appName || null },
        { label: t('farmdev.adspower_task'), value: d.taskType || null },
        { label: t('farmdev.adspower_last_pass'), value: d.lastPassAt ? new Date(d.lastPassAt).toLocaleString() : null },
        { label: t('farmdev.lazy_poll'), value: d.lazyPoll ? t('common.yes') : t('common.no') },
        { label: t('farmdev.lazy_expected'), value: d.expectedDeposits === null ? null : String(d.expectedDeposits) },
        { label: t('farmdev.adspower_last_error'), value: d.lastError || null },
    ]
        .filter((r) => !!r.value);
    showInfoModal(d.name, rows);
}
/** The stage placeholder: browser state as the headline, flow / last pass / error under it. */
function placeholderHtml(d) {
    const lastPass = d.lastPassAt ? formatClock(d.lastPassAt) : '—';
    const flow = d.flowName ? esc(d.flowName) : t('farmdev.adspower_no_flow');
    return `
        <div class="device-player-placeholder">
            <span>${browserGlyphSvg}</span>
            <strong>${d.browserOpen ? t('farmdev.adspower_browser_open') : t('farmdev.adspower_browser_closed')}</strong>
            <span class="aps-meta">${t('farmdev.adspower_flow')}: ${flow}</span>
            <span class="aps-meta">${t('farmdev.adspower_last_pass')}: ${esc(lastPass)}</span>
            ${d.enabled && d.lazyPoll && d.expectedDeposits !== null ? `<span class="aps-meta">${d.expectedDeposits ? `${t('farmdev.lazy_expected')}: ${d.expectedDeposits}` : t('farmdev.lazy_waiting')}</span>` : ''}
            ${d.lastError ? `<span class="aps-meta aps-meta--error" title="${esc(d.lastError)}">${esc(d.lastError)}</span>` : ''}
        </div>`;
}
/** Reflect Start/Stop for the card's current `enabled` (the same look as the duo clicker button). */
function renderClickerBtn(card, d) {
    const btn = card.querySelector('.aps-clicker');
    if (!btn)
        return;
    btn.innerHTML = d.enabled ? clickerStopSvg : clickerStartSvg;
    btn.classList.toggle('is-running', d.enabled);
    btn.title = d.enabled ? t('farmdev.stop_clicker') : t('farmdev.start_clicker');
    btn.setAttribute('aria-label', btn.title);
}
function makeAdspowerDeviceCard(d, farmId, farmLabel) {
    const card = document.createElement('div');
    card.className = `device-card device-card--adspower ${d.online ? 'online' : 'offline'}`;
    card.dataset['udid'] = d.udid;
    const logBtn = can('device.clickerLog')
        ? `<button type="button" class="stream-control-btn aps-clicker-logs" title="${t('farmdev.clicker_logs')}" aria-label="${t('farmdev.clicker_logs')}">${clickerLogSvg}</button>`
        : '';
    card.innerHTML = `
        <div class="device-card__header">
            <span class="status-dot ${d.online ? 'status-dot--green' : 'status-dot--red'}"></span>
            <span class="device-name-wrap">
                ${deviceNameHtml(d.name, d.udid, farmLabel)}
                ${deviceSubtitlesHtml(subtitleData(d))}
            </span>
        </div>
        <div class="device-card__player">
            <div class="device-stream-stage">
                <div class="device-stream-canvas-wrap aps-screen-wrap">${placeholderHtml(d)}</div>
                <div class="device-stream-controls" aria-label="${t('farmdev.device_controls')}">
                    <button type="button" class="stream-control-btn aps-info-btn" title="${t('farmdev.device_info')}" aria-label="${t('farmdev.device_info')}">${infoSvg}</button>
                    <span class="stream-controls__divider"></span>
                    <button type="button" class="stream-control-btn aps-clicker duo-clicker" title="${t('farmdev.start_clicker')}" aria-label="${t('farmdev.start_clicker')}">${clickerStartSvg}</button>
                    <button type="button" class="stream-control-btn aps-clicker-settings" title="${t('farmdev.reconfigure_clicker')}" aria-label="${t('farmdev.reconfigure_clicker')}">${clickerSettingsSvg}</button>
                    ${logBtn}
                    <button type="button" class="stream-control-btn aps-clicker-ops" title="${t('farmdev.operations_registry')}" aria-label="${t('farmdev.operations_registry')}">${clickerRegistrySvg}</button>
                </div>
            </div>
        </div>
    `;
    renderClickerBtn(card, d);
    card.querySelector('.aps-info-btn')?.addEventListener('click', () => showAdspowerInfoModal(d));
    card.querySelector('.aps-clicker')?.addEventListener('click', async () => {
        const btn = card.querySelector('.aps-clicker');
        if (!btn)
            return;
        btn.disabled = true;
        const action = d.enabled ? 'stop' : 'start';
        try {
            const res = await fetch(`/api/farms/${farmId}/clicker/devices/${encodeURIComponent(d.udid)}/${action}`, { method: 'POST' });
            if (res.ok) {
                // Optimistic: the 15s poll confirms once the agent picks the flag up.
                d.enabled = action === 'start';
                card.replaceWith(makeAdspowerDeviceCard(d, farmId, farmLabel));
            }
            else {
                const data = await res.json().catch(() => ({}));
                alert(data.error || t('farmdev.clicker_action_failed'));
            }
        }
        catch { /* network error — leave state unchanged */ }
        finally {
            btn.disabled = false;
        }
    });
    card.querySelector('.aps-clicker-settings')?.addEventListener('click', () => {
        void showClickerRestartModal(farmId, d.udid, d.name, { webUrl: true });
    });
    card.querySelector('.aps-clicker-logs')?.addEventListener('click', () => openClickerLogWindow(d.udid, farmId));
    card.querySelector('.aps-clicker-ops')?.addEventListener('click', () => openClickerOperationsWindow(farmId, d.udid, d.name));
    return card;
}
/** One profile card for a grid that mixes farms (the My Devices board), with the owning
 *  farm shown in front of the name. Same builder as the farm grid uses. */
export function makeAdspowerCardForBoard(d, farmId, farmLabel) {
    return makeAdspowerDeviceCard(d, farmId, farmLabel);
}
export async function fetchAdspowerDevices(farmId) {
    try {
        const r = await fetch(`/api/farms/${farmId}/adspower-devices`);
        if (!r.ok)
            return { devices: [], failed: true };
        const body = await r.json();
        return { devices: body.devices ?? [], failed: false };
    }
    catch {
        return { devices: [], failed: true };
    }
}
function matchesQuery(d) {
    const q = profileQuery.trim().toLowerCase();
    return !q || d.name.toLowerCase().includes(q) || d.udid.toLowerCase().includes(q);
}
/** Paint the cached list through the profile search; `failed` picks the empty-state text. */
function renderAdspowerGrid(farmId, failed) {
    const grid = document.getElementById('device-grid');
    if (!grid || !document.body.contains(grid))
        return;
    const shown = lastDevices.filter(matchesQuery);
    reconcileGrid(grid, shown.map((d) => ({ udid: d.udid, preserve: false, make: () => makeAdspowerDeviceCard(d, farmId) })));
    grid.querySelectorAll(':scope > .empty').forEach((el) => el.remove());
    if (!grid.querySelector('.device-card--adspower')) {
        const empty = document.createElement('p');
        empty.className = 'empty';
        empty.textContent = failed
            ? t('farmdev.adspower_unreachable')
            : (lastDevices.length ? t('farmdev.adspower_no_match') : t('farmdev.adspower_no_devices'));
        grid.appendChild(empty);
    }
}
export async function injectAdspowerDevices(farmId) {
    const { devices, failed } = await fetchAdspowerDevices(farmId);
    if (!failed)
        lastDevices = devices;
    lastFarmId = farmId;
    renderAdspowerGrid(farmId, failed);
}
/**
 * The profile search box in the farm header — AdsPower farms only, so it is mounted
 * here (not in renderFarmDevices, which may not know the farm kind at first paint).
 */
function mountProfileSearch() {
    const header = document.querySelector('.farm-devs-header');
    if (!header || header.querySelector('#adspower-profile-search'))
        return;
    const input = document.createElement('input');
    input.type = 'search';
    input.id = 'adspower-profile-search';
    input.className = 'farm-filter__search';
    input.placeholder = t('farmdev.adspower_search_placeholder');
    input.value = profileQuery;
    input.addEventListener('input', () => {
        profileQuery = input.value;
        if (lastFarmId != null)
            renderAdspowerGrid(lastFarmId, false);
    });
    header.querySelector('#back-btn')?.insertAdjacentElement('afterend', input);
}
/** Stop the active adspower device-grid poll. `keepFarmName` leaves it running when it
 *  matches the farm currently polled (a re-render of the SAME farm keeps the loop). */
export function stopAdspowerDevicePolling(keepFarmName) {
    if (!poll)
        return;
    if (keepFarmName !== undefined && poll.farmName === keepFarmName)
        return;
    window.clearInterval(poll.timer);
    poll = null;
    lastDevices = [];
    profileQuery = '';
}
/** Poll {@link injectAdspowerDevices} while this adspower farm's grid stays mounted. */
export function startAdspowerDevicePolling(farmId, farmName) {
    const grid = document.getElementById('device-grid');
    if (!grid)
        return;
    if (poll && poll.farmName !== farmName)
        stopAdspowerDevicePolling();
    else if (poll)
        window.clearInterval(poll.timer);
    mountProfileSearch();
    const timer = window.setInterval(() => {
        const g = document.getElementById('device-grid');
        if (!g || !document.body.contains(g)) {
            stopAdspowerDevicePolling();
            return;
        }
        void injectAdspowerDevices(farmId);
    }, POLL_MS);
    poll = { farmName, timer };
    void injectAdspowerDevices(farmId);
}
